import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  Modal,
  Input,
  Button,
  Checkbox,
  Form,
  DatePicker,
  Switch,
  Tag,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import moment from "moment";
import { toast } from "react-hot-toast";
import { createReservation } from "../../Store/Apis/ReservationApi";
import { createCompany } from "../../Store/Apis/ReservationApi";
const { TextArea } = Input;

const PaymentModal = ({
  visible,
  onCancel,
  items = [],
  prices = [],
  cartItems = [],
  addOns = [],
}) => {
  const [form] = Form.useForm();
  const [contactNumber, setContactNumber] = useState("");
  // Dynamic payment types from customer data
  const [allowedPaymentTypes, setAllowedPaymentTypes] = useState([]);
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  const navigate = useNavigate();
  const [referenceNumber, setReferenceNumber] = useState("");
  const location = useLocation();
  const { reservationData, rates } = location.state || {};
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);

  // Normalize any incoming phone to ZA format: ensure it starts with 27
  const normalizeToZA = (val) => {
    if (val == null) return "";
    const digits = String(val).replace(/\D/g, "");
    if (digits.startsWith("27")) return digits;
    // drop any leading zeros then prefix '27'
    const stripped = digits.replace(/^0+/, "");
    return `27${stripped}`;
  };

  // Check if this is a company booking and if it's a quotation
  const isCompanyBooking = reservationData?.bookingType === "company";
  const isQuotation = reservationData?.isQuotation === true;
  const statusID = reservationData?.statusID || 11;
  const guestDetails = reservationData?.guestDetails || [];

  // FIXED: Calculate pricing directly from rates and nights
  const calculatePricingFromRates = () => {
    if (!rates)
      return { calculatedItems: [], calculatedPrices: [], roomTotal: 0 };

    const calculatedItems = [];
    const calculatedPrices = [];
    let roomTotal = 0;

    if (isCompanyBooking) {
      // For company bookings, handle individual guest pricing
      Object.entries(rates).forEach(([key, details]) => {
        // Find the corresponding guest for this rate
        const guestIndex = details.index || 0;
        const correspondingGuest = guestDetails[guestIndex];

        if (correspondingGuest) {
          const guestNights =
            correspondingGuest.nights ||
            dayjs(correspondingGuest.checkOutDate).diff(
              dayjs(correspondingGuest.checkInDate),
              "day"
            );
          const roomTypeName =
            details.typeName || (key.includes("_") ? key.split("_")[0] : key);

          calculatedItems.push(
            `${roomTypeName} - ${correspondingGuest.fullName} (${guestNights} nights)`
          );
          const roomPrice = details.price * guestNights;
          calculatedPrices.push(roomPrice);
          roomTotal += roomPrice;
        }
      });
    } else {
      // For individual bookings, use the reservation nights
      const nights = reservationData?.nights || 1;

      Object.entries(rates).forEach(([key, details]) => {
        const roomTypeName =
          details.typeName || (key.includes("_") ? key.split("_")[0] : key);
        calculatedItems.push(roomTypeName);
        const roomPrice = details.price * nights;
        calculatedPrices.push(roomPrice);
        roomTotal += roomPrice;
      });
    }

    // Add add-ons (these don't get multiplied by nights)
    addOns.forEach((addon) => {
      calculatedItems.push(addon.name);
      const addonPrice = addon.rate * (addon.quantity || 1);
      calculatedPrices.push(addonPrice);
    });

    return { calculatedItems, calculatedPrices, roomTotal };
  };

  // Use calculated pricing
  const { calculatedItems, calculatedPrices, roomTotal } =
    calculatePricingFromRates();

  // Use calculated prices if rates are available, otherwise fallback to props
  const finalItems =
    rates && calculatedItems.length > 0 ? calculatedItems : items;
  const finalPrices =
    rates && calculatedPrices.length > 0 ? calculatedPrices : prices;

  const grossTotal = finalPrices.reduce((total, price) => total + price, 0);

  // Calculate VAT
  const vat = finalPrices.reduce(
    (totalVat, price) => totalVat + price * 0.0,
    0
  );
  const finalTotal = grossTotal + vat;

  // Debug logs
  useEffect(() => {
    console.log("PaymentModal - Debug Info:");
    console.log("Rates:", rates);
    console.log("Reservation data:", reservationData);
    console.log("Nights:", reservationData?.nights);
    console.log("Calculated items:", calculatedItems);
    console.log("Calculated prices:", calculatedPrices);
    console.log("Final items:", finalItems);
    console.log("Final prices:", finalPrices);
    console.log("Gross total:", grossTotal);
    console.log("Props items:", items);
    console.log("Props prices:", prices);
  }, [
    rates,
    reservationData,
    calculatedItems,
    calculatedPrices,
    finalItems,
    finalPrices,
    grossTotal,
    items,
    prices,
  ]);

  // Generate reference number on component mount
  useEffect(() => {
    generateReferenceNumber();
  }, []);

  // Log add-ons received from parent component
  useEffect(() => {
    if (addOns && addOns.length > 0) {
      addOns.forEach((addon) => {
        // console.log(`Add-on: ${addon.name}, ParentId: ${addon.parentId}, ChildId: ${addon.childId}, TypeId: ${addon.typeId}, Quantity: ${addon.quantity}, Rate: ${addon.rate}`);
      });
    }
  }, [addOns]);

  // Prepopulate form fields if customer info exists
  useEffect(() => {
    if (
      reservationData?.customerInfo?.isExisting &&
      reservationData?.customerInfo?.customerData
    ) {
      const customerData = reservationData.customerInfo.customerData;
      const isCompany = reservationData.customerInfo.customerType === "company";

      console.log("Prepopulating fields with customer data:", customerData);

      if (isCompany) {
        // Prepopulate company fields
        form.setFieldsValue({
          customerName: customerData.companyName,
          customerEmail: customerData.companyEmailAddress,
          companyAddress: customerData.companyAddress,
        });
        setContactNumber(normalizeToZA(customerData.companyPhoneNumber || ""));
        console.log("Set company phone:", customerData.companyPhoneNumber);
      } else {
        // Prepopulate individual customer fields
        form.setFieldsValue({
          customerName: customerData.customerName || customerData.name,
          customerEmail: customerData.email || customerData.customerEmail,
          customerAddress: customerData.address,
        });
        // Try multiple possible phone field names
        const phoneNumber =
          customerData.phoneNumber || customerData.phone || "";
        setContactNumber(normalizeToZA(phoneNumber));
        console.log("Set individual phone:", phoneNumber);
      }
    }
  }, [reservationData, form]);

  // Helper function to determine if fields should be disabled/readonly
  const shouldDisableField = (fieldName) => {
    const existingData = reservationData?.customerInfo?.customerData;
    if (!reservationData?.customerInfo?.isExisting || !existingData)
      return false;

    const isCompany = reservationData.customerInfo.customerType === "company";

    if (isCompany) {
      return (
        (fieldName === "customerName" && existingData.companyName) ||
        (fieldName === "customerEmail" && existingData.companyEmailAddress) ||
        (fieldName === "companyAddress" && existingData.companyAddress)
      );
    } else {
      return (
        (fieldName === "customerName" &&
          (existingData.customerName || existingData.name)) ||
        (fieldName === "customerEmail" &&
          (existingData.email || existingData.customerEmail)) ||
        (fieldName === "customerAddress" && existingData.address)
      );
    }
  };

  const generateReferenceNumber = () => {
    const today = new Date();
    const year = today.getFullYear().toString().substr(-2);
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    // For the counter, use a 3-digit number
    const count = String(Math.floor(Math.random() * 900) + 100);

    const prefix = isQuotation ? "QUO" : "RES";
    const newReference = `${prefix}${year}${month}${day}${count}`;
    setReferenceNumber(newReference);

    // Set the UTR number field value
    form.setFieldsValue({ utrNumber: newReference });
  };

  // Parse payment types from customer data
  useEffect(() => {
    const cd = reservationData?.customerInfo?.customerData || {};

    // Accept multiple possible field names from backend
    const rawTypes =
      cd?.payment_types ??
      cd?.payment_Terms ??
      cd?.paymentTerms ??
      cd?.paymentterms ??
      cd?.paymenttypes;

    // Normalize into an array of non-empty strings
    let parsed = [];
    if (Array.isArray(rawTypes)) {
      parsed = rawTypes.map((s) => String(s).trim()).filter(Boolean);
    } else if (typeof rawTypes === "string") {
      parsed = rawTypes
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }

    // Check if account is on hold
    const creditAccStatus = (cd?.creditAccStatus || "").trim();
    const isOnHold = creditAccStatus.toLowerCase() === "on hold";

    // Debug: Log account status
    console.log("PaymentModal - Account status check:", {
      creditAccStatus,
      isOnHold,
      customerData: cd,
    });

    if (isOnHold) {
      console.log(
        "Account is on hold - Account Payment will be shown as disabled"
      );
    }

    // Note: We don't filter out Account Payment here anymore.
    // Instead, we'll show it as disabled in the UI when account is on hold.

    // If no explicit terms provided (empty string/null/undefined), default to 3 options (exclude Account Payment)
    const fallbackNoAccount = ["Cash", "Card", "Bank Transfer"];
    const allowed = parsed.length ? parsed : fallbackNoAccount;

    setAllowedPaymentTypes(allowed);
    // Keep previous selection only if still allowed and not disabled due to account hold
    setSelectedPaymentType((prev) => {
      // If previous selection was Account Payment and account is on hold, select a different option
      if (prev && prev.toLowerCase().includes("account") && isOnHold) {
        return (
          allowed.find((type) => !type.toLowerCase().includes("account")) ||
          allowed[0]
        );
      }
      return allowed.includes(prev) ? prev : allowed[0];
    });
  }, [reservationData]);

  const customerData = reservationData?.customerInfo?.customerData || {};
  const parseAmount = (v) => {
    if (v === null || v === undefined) return null;
    const cleaned = String(v).replace(/[^0-9.-]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  };
  const creditLimit = useMemo(() => {
    const raw = customerData?.currentLimit ?? customerData?.creditLimit ?? null;
    return parseAmount(raw);
  }, [customerData]);
  const creditAccStatus = (customerData?.creditAccStatus || "").trim();
  const isOnHold = creditAccStatus.toLowerCase() === "on hold";

  const handleSave = async (bypassCreditCheck = false) => {
    try {
      console.log("Starting handleSave...");

      // Validate form fields
      await form.validateFields();
      const formValues = form.getFieldsValue();

      let companyID = null;
      let customerID = null;

      // Check if we have existing customer info
      const existingCustomerInfo = reservationData?.customerInfo;
      const isExistingCustomer = existingCustomerInfo?.isExisting;
      const existingCustomerData = existingCustomerInfo?.customerData;

      if (isCompanyBooking) {
        if (isExistingCustomer && existingCustomerData?.companyID) {
          // Use existing company ID
          companyID = existingCustomerData.companyID;
          console.log("Using existing company ID:", companyID);
        } else {
          // Create new company
          console.log("Creating new company...");
          const companyData = {
            companyName: formValues.customerName,
            companyAddress:
              formValues.companyAddress || formValues.customerAddress,
            companyPhoneNumber: contactNumber,
            companyEmailAddress: formValues.customerEmail,
          };

          const companyResponse = await createCompany(companyData);
          if (companyResponse && companyResponse.companyID) {
            companyID = companyResponse.companyID;
            console.log("New company created with ID:", companyID);
          } else {
            toast.error(
              "Failed to create company. Cannot proceed with reservation."
            );
            return;
          }
        }
      } else {
        // For individual bookings, get customer ID if existing
        if (isExistingCustomer && existingCustomerData?.customerID) {
          customerID = existingCustomerData.customerID;
          console.log("Using existing customer ID:", customerID);
        }
      }

      // Log the rates structure to better understand it
      console.log("Rates structure:", rates);

      // FIXED: Handle flat structure rates transformation
      let ratesArray = [];

      if (rates) {
        // For flat structure, iterate directly over rates
        Object.entries(rates).forEach(([key, details]) => {
          ratesArray.push({
            name:
              details.typeName || (key.includes("_") ? key.split("_")[0] : key),
            childId: details.childId || 0,
            typeId: details.typeId || 1,
            rate: details.price || 0,
            parentId: details.parentId || 0,
          });
        });
      }

      console.log("Transformed rates array:", ratesArray);

      const addOnsArray = Array.isArray(addOns) ? addOns : [];

      // Format the date properly if it exists
      const paymentDate = formValues.date
        ? formValues.date.format("YYYY-MM-DD")
        : new Date().toISOString().split("T")[0];

      // Determine payment type based on checkboxes
      // Map selected payment type to backend codes
      const mapPaymentTypeCode = (label) => {
        const key = (label || "").toLowerCase();
        if (key.includes("bank") || key.includes("eft")) return 3; // Bank Transfer / EFT
        if (key.includes("account")) return 4; // Account Payment
        // Cash/Card/Arrival default to pay on arrival
        return 1;
      };

      // Enforce credit rules for Account Payment
      if (selectedPaymentType.toLowerCase().includes("account")) {
        console.log("[PaymentModal] Credit check", {
          selectedPaymentType,
          finalTotal,
          creditLimit,
          bypassCreditCheck,
          isOnHold,
        });
        if (isOnHold) {
          toast.error(
            "Your credit account is on hold. Please select another payment option."
          );
          return;
        }
        if (!bypassCreditCheck) {
          const totalNum = parseAmount(finalTotal);
          const limitNum = parseAmount(creditLimit);
          if (
            limitNum != null &&
            Number.isFinite(limitNum) &&
            totalNum > limitNum
          ) {
            setShowCreditConfirm(true);
            return;
          }
        }
      }

      const paymentType = mapPaymentTypeCode(selectedPaymentType);
      const payNow =
        selectedPaymentType.toLowerCase().includes("bank") ||
        selectedPaymentType.toLowerCase().includes("eft")
          ? 1
          : 0;

      // Determine if credit limit was exceeded and user chose to proceed
      const isAccountSelected = selectedPaymentType
        .toLowerCase()
        .includes("account");
      const totalNum = parseAmount(finalTotal);
      const limitNum = parseAmount(creditLimit);
      const didExceedLimit =
        isAccountSelected &&
        limitNum != null &&
        Number.isFinite(limitNum) &&
        totalNum > limitNum &&
        bypassCreditCheck === true;

      // Calculate overall dates for company bookings
      let overallCheckIn = null;
      let overallCheckOut = null;

      if (isCompanyBooking && guestDetails.length > 0) {
        // Find the earliest check-in date
        overallCheckIn = guestDetails.reduce((earliest, guest) => {
          const guestCheckIn = new Date(guest.checkInDate);
          return !earliest || guestCheckIn < new Date(earliest)
            ? guest.checkInDate
            : earliest;
        }, null);

        // Find the latest check-out date
        overallCheckOut = guestDetails.reduce((latest, guest) => {
          const guestCheckOut = new Date(guest.checkOutDate);
          return !latest || guestCheckOut > new Date(latest)
            ? guest.checkOutDate
            : latest;
        }, null);
      }

      // Prepare subCustomers for company bookings
      const subCustomers = isCompanyBooking
        ? guestDetails.map((guest) => ({
            companyID: companyID,
            customerCheckIn: guest.checkInDate,
            customerCheckOut: guest.checkOutDate,
            customerName: guest.fullName,
            customerEmail: guest.email,
          }))
        : [];

      // Construct the reservation data to send to the API
      const reservationDataToSubmit = {
        customerName: formValues.customerName,
        customerID: customerID || 0, // Add customer ID for individual bookings
        companyID: companyID || 0,
        subCustomers: subCustomers,
        createdBy: 0,
        clientType:
          reservationData?.customerInfo?.customerType ||
          (reservationData?.bookingType === "company"
            ? "company"
            : "individual"),
        subTotal: grossTotal,
        outletId: reservationData?.outletId,
        statusId: statusID,
        customerAddress:
          formValues.customerAddress || formValues.companyAddress,
        checkInDate: isCompanyBooking
          ? overallCheckIn
          : reservationData?.startDate,
        checkOutDate: isCompanyBooking
          ? overallCheckOut
          : reservationData?.endDate,
        phoneNumber: contactNumber,
        email: formValues.customerEmail,
        discount: 0,
        discountType: 2,
        paymentDate: paymentDate,
        receivedAmount: 0,
        payAmount: finalTotal,
        paymentType,
        paymentNote: formValues.paymentNote || "",
        transactionId: "",
        utrNumber: formValues.utrNumber || referenceNumber,
        financialYearId: 0,
        isDeleted: 0,
        payNow,
        exceed_Limit: didExceedLimit ? "yes" : "no",
        taxAmount: vat,
        taxPercentage: 15,
        roomQuantity: reservationData?.rooms || 0,
        serviceDetails: [
          ...ratesArray.map((rate) => ({
            serviceID: rate.childId,
            typeID: rate.typeId,
            serviceQuantity: 1,
            serviceRate: rate.rate,
            detailPrice: rate.rate,
            parentID: rate.parentId,
          })),
          ...addOnsArray.map((addon) => ({
            serviceID: addon.childId,
            typeID: addon.typeId,
            serviceQuantity: addon.quantity || 1,
            serviceRate: addon.rate,
            detailPrice: addon.rate * (addon.quantity || 1),
            parentID: addon.parentId,
          })),
        ],
      };

      console.log("reservationDataToSubmit:", reservationDataToSubmit);

      // Call the API to create the reservation
      const response = await createReservation(reservationDataToSubmit);
      console.log("Reservation created:", response);

      // Handle success or failure accordingly
      if (response) {
        const successMessage = isQuotation
          ? "Quotation created successfully!"
          : "Reservation created successfully!";
        toast.success(successMessage, { duration: 10000 });
        navigate("/");
        onCancel();
      } else {
        const errorMessage = isQuotation
          ? "Error creating quotation"
          : "Error creating reservation";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error in handleSave:", error);
      const errorMessage = isQuotation
        ? `Error creating quotation: ${error.message}`
        : `Error creating reservation: ${error.message}`;
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <StyledModal
        title={
          <ModalTitleContainer>
            <ModalTitle>
              {isQuotation ? "Quotation Details" : "Payment Details"}
            </ModalTitle>
            {isQuotation && (
              <QuotationTag>
                <Tag
                  color="orange"
                  style={{ margin: 0, fontSize: "12px", fontWeight: "bold" }}
                >
                  QUOTATION
                </Tag>
              </QuotationTag>
            )}
          </ModalTitleContainer>
        }
        visible={visible}
        onCancel={onCancel}
        footer={null}
        width="75vw"
        style={{ top: "5vh" }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ utrNumber: referenceNumber }}
        >
          <StyledContainer>
            {/* Company Booking Section - Only show if it's a company booking */}
            {isCompanyBooking && (
              <CompanyBookingSection>
                <CompanyBookingHeader>
                  <CompanyBookingTitle>Company Booking</CompanyBookingTitle>
                  <CompanyBookingSwitch>
                    <Switch checked={true} disabled={true} size="small" />
                    <SwitchLabel>Company Mode</SwitchLabel>
                  </CompanyBookingSwitch>
                </CompanyBookingHeader>

                <GuestDetailsList>
                  <GuestDetailsTitle>
                    Guest Details for Each Room
                  </GuestDetailsTitle>
                  {guestDetails.map((guest, index) => (
                    <GuestDetailCard key={index}>
                      <GuestCardHeader>
                        Room {guest.roomNumber || index + 1}
                      </GuestCardHeader>
                      <GuestInfo>
                        <GuestInfoRow>
                          <GuestLabel>Name:</GuestLabel>
                          <GuestValue>{guest.fullName}</GuestValue>
                        </GuestInfoRow>
                        <GuestInfoRow>
                          <GuestLabel>Email:</GuestLabel>
                          <GuestValue>{guest.email}</GuestValue>
                        </GuestInfoRow>
                        <GuestInfoRow>
                          <GuestLabel>Check-in:</GuestLabel>
                          <GuestValue>{guest.checkInDate}</GuestValue>
                        </GuestInfoRow>
                        <GuestInfoRow>
                          <GuestLabel>Check-out:</GuestLabel>
                          <GuestValue>{guest.checkOutDate}</GuestValue>
                        </GuestInfoRow>
                      </GuestInfo>
                    </GuestDetailCard>
                  ))}
                </GuestDetailsList>
              </CompanyBookingSection>
            )}

            {/* FIXED: Display items and prices using calculated values */}
            <ItemsList>
              {finalItems.map((item, index) => (
                <ItemRow key={index}>
                  <ItemName>{item}</ItemName>
                  <ItemPrice>
                    R {finalPrices[index]?.toFixed(2) || "0.00"}
                  </ItemPrice>
                </ItemRow>
              ))}
            </ItemsList>

            <FieldsRow>
              <FormField span={12}>
                <Form.Item
                  label={
                    <span>
                      {isCompanyBooking ? "Company Name" : "Customer Name"}
                      {reservationData?.customerInfo?.isExisting && (
                        <Tag
                          color="blue"
                          size="small"
                          style={{ marginLeft: 8 }}
                        >
                          Existing
                        </Tag>
                      )}
                    </span>
                  }
                  name="customerName"
                  rules={[
                    {
                      required: true,
                      message: `Please enter ${
                        isCompanyBooking ? "company" : "customer"
                      } name`,
                    },
                  ]}
                >
                  <Input
                    placeholder={`Enter ${
                      isCompanyBooking ? "company" : "customer"
                    } name`}
                    disabled={shouldDisableField("customerName")}
                  />
                </Form.Item>
              </FormField>
              <FormField span={12}>
                <Form.Item
                  label={
                    <span>
                      {isCompanyBooking ? "Company Email" : "Customer Email"}
                      {reservationData?.customerInfo?.isExisting && (
                        <Tag
                          color="blue"
                          size="small"
                          style={{ marginLeft: 8 }}
                        >
                          Existing
                        </Tag>
                      )}
                    </span>
                  }
                  name="customerEmail"
                  rules={[
                    {
                      required: true,
                      type: "email",
                      message: "Please enter a valid email",
                    },
                  ]}
                >
                  <Input
                    placeholder={`Enter ${
                      isCompanyBooking ? "company" : "customer"
                    } email`}
                    disabled={shouldDisableField("customerEmail")}
                  />
                </Form.Item>
              </FormField>
            </FieldsRow>

            {/* Company Address Field - Only show for company bookings */}
            {isCompanyBooking && (
              <FieldsRow>
                <FormField span={24}>
                  <Form.Item
                    label={
                      <span>
                        Company Address
                        {reservationData?.customerInfo?.isExisting && (
                          <Tag
                            color="blue"
                            size="small"
                            style={{ marginLeft: 8 }}
                          >
                            Existing
                          </Tag>
                        )}
                      </span>
                    }
                    name="companyAddress"
                    rules={[
                      {
                        required: true,
                        message: "Please enter company address",
                      },
                    ]}
                  >
                    <TextArea
                      placeholder="Enter company address"
                      rows={3}
                      disabled={shouldDisableField("companyAddress")}
                      style={{
                        borderRadius: "5px",
                        border: "1px solid #CACACA",
                        outline: "none",
                        fontSize: "14px",
                      }}
                    />
                  </Form.Item>
                </FormField>
              </FieldsRow>
            )}

            <FieldsRow>
              <FormField span={12}>
                <Form.Item
                  label={
                    <span>
                      {isCompanyBooking
                        ? "Company Phone Number"
                        : "Phone Number"}
                      {reservationData?.customerInfo?.isExisting && (
                        <Tag
                          color="blue"
                          size="small"
                          style={{ marginLeft: 8 }}
                        >
                          Existing
                        </Tag>
                      )}
                    </span>
                  }
                  // name="phoneNumber"
                  // rules={[
                  //   { required: true, message: "Please enter Phone Number" },
                  // ]}
                >
                  <PhoneInput
                    country={"za"}
                    value={contactNumber}
                    disableDropdown
                    countryCodeEditable={false}
                    copyNumbersOnly
                    onChange={(value) => {
                      const normalized = normalizeToZA(value);
                      setContactNumber(normalized);
                    }}
                    disabled={
                      reservationData?.customerInfo?.isExisting &&
                      (reservationData?.customerInfo?.customerData
                        ?.companyPhoneNumber ||
                        reservationData?.customerInfo?.customerData
                          ?.phoneNumber ||
                        reservationData?.customerInfo?.customerData?.phone)
                    }
                    inputStyle={{
                      width: "100%",
                      height: "35px",
                      borderRadius: "5px",
                      border: "1px solid #CACACA",
                      outline: "none",
                      paddingLeft: "48px",
                      fontSize: "14px",
                      backgroundColor:
                        reservationData?.customerInfo?.isExisting &&
                        (reservationData?.customerInfo?.customerData
                          ?.companyPhoneNumber ||
                          reservationData?.customerInfo?.customerData
                            ?.phoneNumber ||
                          reservationData?.customerInfo?.customerData?.phone)
                          ? "#f5f5f5"
                          : "white",
                    }}
                  />
                </Form.Item>
              </FormField>
              <FormField span={12}>
                <Form.Item label="Date" name="date">
                  <DatePicker
                    defaultValue={moment()}
                    format="YYYY-MM-DD"
                    style={{
                      width: "100%",
                      height: "35px",
                      borderRadius: "5px",
                      border: "1px solid #CACACA",
                      outline: "none",
                      fontSize: "14px",
                    }}
                    disabled={true}
                  />
                </Form.Item>
              </FormField>
            </FieldsRow>

            <Summary>
              <SummaryItem>
                <SummaryLabel>Sub Total:</SummaryLabel>
                <SummaryValue>R {grossTotal.toFixed(2)}</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>VAT (0%):</SummaryLabel>
                <SummaryValue>R {vat.toFixed(2)}</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Gross Total:</SummaryLabel>
                <SummaryValue>R {finalTotal.toFixed(2)}</SummaryValue>
              </SummaryItem>
            </Summary>

            {/* Show payment options for reservations and quotations */}
            <PaymentContainer>
              <PaymentHeader>
                <PaymentTypeLabel>Payment Type</PaymentTypeLabel>
                <BalancePill>
                  <BalanceCaption>Balance Due:</BalanceCaption>
                  <BalanceAmount>R {finalTotal.toFixed(2)}</BalanceAmount>
                </BalancePill>
              </PaymentHeader>

              <PaymentOptionsContainer>
                {allowedPaymentTypes.map((type) => {
                  const isSelected = selectedPaymentType === type;
                  const lower = type.toLowerCase();
                  const title = type;

                  // Check if this is an Account Payment option and account is on hold
                  const isAccountPayment = lower.includes("account");
                  const isDisabled = isAccountPayment && isOnHold;

                  const description = lower.includes("bank")
                    ? "Make a bank transfer to confirm your reservation"
                    : lower.includes("account")
                    ? isOnHold
                      ? "Account is on hold - Contact Credit Manager to resolve"
                      : "Charge to your company account for later billing"
                    : lower.includes("card")
                    ? "Pay with card on arrival"
                    : lower.includes("cash")
                    ? "Pay cash on arrival"
                    : "Pay this way";

                  return (
                    <PaymentOptionCard
                      key={type}
                      isSelected={isSelected && !isDisabled}
                      onClick={() => {
                        if (!isDisabled) {
                          setSelectedPaymentType(type);
                        }
                      }}
                      style={{
                        opacity: isDisabled ? 0.6 : 1,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                      }}
                    >
                      <StyledCheckbox
                        checked={isSelected && !isDisabled}
                        disabled={isDisabled}
                        onChange={() => {
                          if (!isDisabled) {
                            setSelectedPaymentType(type);
                          }
                        }}
                      />
                      <PaymentContent>
                        <PaymentOptionTitle
                          style={{ color: isDisabled ? "#999" : "inherit" }}
                        >
                          {title} {isDisabled && "(On Hold)"}
                        </PaymentOptionTitle>
                        <PaymentDescription
                          style={{ color: isDisabled ? "#999" : "inherit" }}
                        >
                          {description}
                        </PaymentDescription>
                      </PaymentContent>
                    </PaymentOptionCard>
                  );
                })}
              </PaymentOptionsContainer>

              {selectedPaymentType.toLowerCase().includes("bank") && (
                <BankDetailsContainer>
                  <BankDetailsTitle>Account Details</BankDetailsTitle>
                  <BankDetailsInfo>
                    <DetailRow>
                      <DetailLabel>Bank:</DetailLabel>
                      <DetailValue>ABSA Bank</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Account Name:</DetailLabel>
                      <DetailValue>Central Office</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Account Number:</DetailLabel>
                      <DetailValue>4055936513</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Branch Code:</DetailLabel>
                      <DetailValue>509243</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Reference:</DetailLabel>
                      <DetailValue>{referenceNumber}</DetailValue>
                    </DetailRow>
                  </BankDetailsInfo>
                </BankDetailsContainer>
              )}
            </PaymentContainer>

            <Form.Item
              label={isQuotation ? "Quote Number" : "Invoice Number"}
              name="utrNumber"
            >
              <Input
                placeholder={
                  isQuotation
                    ? "Quote reference number"
                    : "Enter Unique Transaction Reference Number"
                }
                value={referenceNumber}
                disabled={true}
              />
            </Form.Item>

            <ButtonContainer>
              <CancelButton onClick={onCancel}>Cancel</CancelButton>
              <SaveButton onClick={() => handleSave()}>
                {isQuotation ? "Generate Quote" : "Submit"}
              </SaveButton>
            </ButtonContainer>
          </StyledContainer>
        </Form>
      </StyledModal>
      <Modal
        visible={showCreditConfirm}
        onCancel={() => setShowCreditConfirm(false)}
        title="Credit limit exceeded"
        footer={[
          <Button key="back" onClick={() => setShowCreditConfirm(false)}>
            Back
          </Button>,
          <Button
            key="proceed"
            type="primary"
            onClick={async () => {
              setShowCreditConfirm(false);
              toast.success("Submitting for Credit Manager approval...");
              await handleSave(true);
            }}
          >
            Proceed
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 8 }}>
          Your total (R {finalTotal.toFixed(2)}) exceeds your current credit
          limit (R{" "}
          {Number.isFinite(creditLimit || NaN)
            ? creditLimit.toFixed(2)
            : "0.00"}
          ).
        </p>
        <p style={{ marginBottom: 0 }}>
          You can proceed and your booking will require Credit Manager approval,
          or click Back to choose another payment option.
        </p>
      </Modal>
    </>
  );
};

// New styled components for modal title
const ModalTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalTitle = styled.span`
  font-size: 18px;
  font-weight: bold;
`;

const QuotationTag = styled.div`
  display: flex;
  align-items: center;
`;

// Add new styled components for Company Booking section
const CompanyBookingSection = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #044c7c;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(4, 76, 124, 0.1);
`;

const CompanyBookingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #044c7c;
`;

const CompanyBookingTitle = styled.h3`
  margin: 0;
  color: #044c7c;
  font-size: 18px;
  font-weight: 600;
`;

const CompanyBookingSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SwitchLabel = styled.span`
  color: #044c7c;
  font-weight: 500;
  font-size: 14px;
`;

const GuestDetailsList = styled.div`
  margin-top: 15px;
`;

const GuestDetailsTitle = styled.h4`
  color: #044c7c;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const GuestDetailCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const GuestCardHeader = styled.div`
  font-weight: 600;
  color: #044c7c;
  margin-bottom: 8px;
  font-size: 14px;
`;

const GuestInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GuestInfoRow = styled.div`
  display: flex;
  gap: 8px;
`;

const GuestLabel = styled.span`
  font-weight: 500;
  color: #666;
  min-width: 60px;
`;

const GuestValue = styled.span`
  color: #333;
`;

// Keep all existing styled components
const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: #f8f9fa;
    border-radius: 10px;
    height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .ant-modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;

    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
  }

  .ant-modal-header {
    background: #044c7c;
    border-radius: 10px 10px 0 0;
    border-bottom: none;
    padding: 16px 24px;
    margin-top: -21px;
    height: 60px;
    margin-left: -24px;
    width: calc(100% + 48px);
    flex-shrink: 0;
  }

  .ant-modal-title {
    color: white;
    font-weight: bold;
    font-size: 18px;
    padding: 0 8px;
  }

  .ant-modal-close {
    top: 14px;
    right: 20px;
  }

  .ant-modal-close-x {
    color: white;
    font-size: 16px;
    width: 40px;
    height: 40px;
    line-height: 40px;
  }

  /* Hide default modal scrollbars */
  .ant-modal-wrap {
    overflow: hidden;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FieldsRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const FormField = styled.div`
  flex: ${(props) => props.span / 24};
`;

const Summary = styled.div`
  background: rgba(204, 148, 20, 0.08);
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #cc9414;
  margin: 10px 0;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 5px 0;
`;

const SummaryLabel = styled.span`
  font-weight: 600;
  color: #333;
`;

const SummaryValue = styled.span`
  font-weight: 500;
`;

// New payment section styling
const PaymentContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin: 15px 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid #e8e8e8;
`;

const PaymentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #f2f7fc;
  border-bottom: 1px solid #e8e8e8;
`;

const PaymentTypeLabel = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #044c7c;
`;

const BalancePill = styled.div`
  background: linear-gradient(135deg, #cc9414, #b68210);
  border-radius: 20px;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
`;

const BalanceCaption = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 500;
`;

const BalanceAmount = styled.span`
  color: white;
  font-size: 15px;
  font-weight: 700;
`;

const PaymentOptionsContainer = styled.div`
  display: flex;
  gap: 15px;
  padding: 20px;
`;

const PaymentOptionCard = styled.div`
  flex: 1;
  border: 1px solid ${(props) => (props.isSelected ? "#044c7c" : "#e0e0e0")};
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.isSelected ? "rgba(4, 76, 124, 0.05)" : "white"};

  &:hover {
    border-color: #044c7c;
    background-color: ${(props) =>
      props.isSelected ? "rgba(4, 76, 124, 0.05)" : "rgba(4, 76, 124, 0.02)"};
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  }
`;

const PaymentContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PaymentOptionTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #333;
  margin-bottom: 6px;
`;

const PaymentDescription = styled.div`
  font-size: 13px;
  color: #666;
  line-height: 1.4;
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    border-radius: 4px;
    width: 18px;
    height: 18px;
    border: 2px solid #044c7c;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #044c7c;
    border-color: #044c7c;
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    border-color: #ffffff;
  }

  &:hover .ant-checkbox-inner {
    border-color: #044c7c;
  }
`;

const BankDetailsContainer = styled.div`
  background: rgba(4, 76, 124, 0.05);
  padding: 15px 20px;
  margin: 0;
  border-top: 1px solid #e0e0e0;
`;

const BankDetailsTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #044c7c;
  margin-bottom: 12px;
`;

const BankDetailsInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 10px;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  min-width: 120px;
  color: #555;
`;

const DetailValue = styled.span`
  font-weight: 500;
  color: #333;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
`;

const SaveButton = styled(Button)`
  background-color: #238b04;
  border-color: #238b04;
  color: white;
  font-weight: 500;
  height: 38px;
  min-width: 100px;

  &:hover,
  &:focus {
    background-color: #1c7003;
    border-color: #1c7003;
    color: white;
  }
`;

const CancelButton = styled(Button)`
  background-color: white;
  border-color: #044c7c;
  color: #044c7c;
  font-weight: 500;
  height: 38px;
  min-width: 100px;

  &:hover,
  &:focus {
    background-color: #f0f8ff;
    border-color: #044c7c;
    color: #044c7c;
  }
`;

const ItemsList = styled.div`
  margin-bottom: 20px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 10px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const ItemName = styled.span`
  font-weight: 500;
`;

const ItemPrice = styled.span`
  font-weight: 500;
  color: #333;
`;

export default PaymentModal;
